import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, CalendarCheck, CreditCard, MessageCircle, Copy } from "lucide-react";
import { Reveal, Label, CurrencyToggle } from "./Reveal";
import { createServiceOrder, verifyServicePayment, redeemPromo, quotePromo, holdSlot, fetchPublicConfig } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

const SERVICES = {
  transformation: {
    name: "Mother's Transformation Journey",
    tagline: "A 2-Month Personal Transformation Journey · 6 Sessions",
    duration: "90 minutes",
    lead: "A space for mothers to pause, reflect and reconnect with themselves — beyond the roles they carry every day.",
    features: [
      "6 personalised 1:1 sessions over 2 months",
      "Clarity on patterns, boundaries, identity and self-worth",
      "Includes Mumma Can Heal Me — the ebook",
    ],
    cta: "Begin Your Journey",
    sub: "For mothers ready to come back to themselves.",
    flagship: true,
    prices: {
      inr: { id: "transformation_inr", label: "₹25,000" },
      usd: { id: "transformation_usd", label: "$1,199" },
    },
    details: [
      "A space for mothers to pause, reflect and reconnect with themselves — beyond the roles they carry every day.",
      "This journey explores the areas of life that may be keeping you stuck, overwhelmed or disconnected from yourself, and gently moves you towards greater clarity, self-awareness and alignment.",
    ],
    listTitle: "We may explore",
    list: [
      "Relationships and family dynamics",
      "Emotional patterns and recurring triggers",
      "Self-worth, confidence and identity",
      "Boundaries and people-pleasing",
      "Stress, overwhelm and burnout",
      "Work, purpose and life transitions",
      "Communication and difficult relationships",
      "Limiting beliefs and recurring patterns",
      "Past experiences that continue to influence the present",
      "Finding balance between motherhood and self",
      "Personal growth, choices and decision-making",
      "Creating a life that feels more intentional and fulfilling",
    ],
    closing: "This is not about becoming a different person. It is about coming back to yourself, with greater awareness, clarity and choice.",
    format: "Format: 6 personalised 1:1 sessions over 2 months",
  },
  consultation: {
    name: "Navigating Parenting Challenges",
    tagline: "1:1 Parenting Consultation · 60 minutes",
    duration: "60 minutes",
    lead: "A calm, judgement-free space to understand what is happening beneath a parenting challenge, and find a clearer way forward.",
    features: [
      "One private 60-minute 1:1 session",
      "Bring any parenting concern currently on your mind",
      "Understanding first, behaviour second",
    ],
    cta: "Book a Consultation",
    sub: "One session. One concern. A clearer way forward.",
    flagship: false,
    prices: {
      inr: { id: "consultation_inr", label: "₹3,000" },
      usd: { id: "consultation_usd", label: "$149" },
    },
    details: [
      "Parenting can bring questions we never expected to have, and moments when we simply don't know what to do next.",
      "This session offers a calm, judgement-free space to understand what is happening beneath a parenting challenge and explore a more conscious way forward.",
    ],
    listTitle: "Bring any parenting concern that is currently on your mind, including",
    list: [
      "Tantrums, meltdowns and big emotions",
      "Not listening or repeated resistance",
      "Anger, rudeness or challenging behaviour",
      "Screen dependence and difficult transitions",
      "Sibling conflicts and comparison",
      "Separation anxiety and fears",
      "School-related challenges",
      "Friendship, peer pressure or bullying",
      "Confidence and self-esteem",
      "Giving up easily or lack of resilience",
      "Lying, hiding things or testing boundaries",
      "Power struggles and repeated conflicts",
      "Positive discipline and setting loving limits",
      "Communication and connection",
      "Changes in family dynamics or major transitions",
      "Any behaviour that leaves you confused, concerned or overwhelmed",
    ],
    closing: "The focus is not simply on changing the child's behaviour. It is on understanding the child, understanding the parent-child dynamic, and finding a response that creates greater connection, clarity and cooperation.",
    format: "One session. One concern. A clearer way forward.",
  },
};

const CHILD_AGES = ["Under 1 year", "1–3 years", "4–6 years", "7–10 years", "11–13 years", "14–18 years", "Multiple ages"];

const EMPTY_FORM = { name: "", email: "", whatsapp: "", childAge: "", issue: "", promo: "" };

export default function ServicesWork() {
  const [currency, setCurrency] = useState("inr");
  const [active, setActive] = useState(null); // "transformation" | "consultation" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("choose"); // choose | schedule | pay | thanks
  const [promo, setPromo] = useState(null); // {discount_pct, final_amount, currency, free} | null
  const [copied, setCopied] = useState(false);
  const [calUrls, setCalUrls] = useState({});

  useEffect(() => { loadRazorpay(); }, []);
  useEffect(() => {
    fetchPublicConfig()
      .then((r) => setCalUrls(r.data?.calendly || {}))
      .catch(() => {});
  }, []);

  const open = (id) => { setActive(id); setStage("choose"); setForm(EMPTY_FORM); setPromo(null); };

  useEffect(() => {
    const handler = (e) => open(e.detail === "transformation" ? "transformation" : "consultation");
    window.addEventListener("ma:open-service", handler);
    return () => window.removeEventListener("ma:open-service", handler);
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active]);

  const packageId = active ? SERVICES[active].prices[currency].id : null;
  const calendlyUrl = active ? (calUrls[active] || "") : "";
  const calUrl = calendlyUrl
    ? `${calendlyUrl}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1&name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`
    : "";

  const fmt = (v) => {
    const n = Number(v);
    const str = Number.isInteger(n) ? n.toLocaleString("en-IN") : n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    return (promo?.currency || (currency === "inr" ? "INR" : "USD")) === "INR" ? `₹${str}` : `$${str}`;
  };

  // Step 2: after a time is blocked on Calendly, move to payment (or finish free)
  useEffect(() => {
    if (stage !== "schedule" || !active) return;
    const onMsg = (e) => {
      if (e.origin !== "https://calendly.com") return;
      if (e?.data?.event !== "calendly.event_scheduled") return;
      holdSlot({
        package_id: packageId, email: form.email, name: form.name,
        whatsapp: form.whatsapp, origin_url: window.location.origin,
        event_uri: e?.data?.payload?.event?.uri, invitee_uri: e?.data?.payload?.invitee?.uri,
      }).catch(() => {});
      if (promo?.free) {
        (async () => {
          try {
            await redeemPromo({
              package_id: packageId, code: form.promo.trim(), email: form.email, name: form.name,
              whatsapp: form.whatsapp, child_age: form.childAge || undefined, issue: form.issue || undefined,
              origin_url: window.location.origin,
            });
          } catch { /* slot is booked; owner notified via Calendly */ }
          setStage("thanks");
        })();
      } else {
        setStage("pay");
        toast.success("Time blocked! One last step, complete your payment to confirm.");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, active, promo]);

  // Step 1: validate details (+ promo), then go block a time
  const proceedToSchedule = async () => {
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Please enter your name, email and WhatsApp number.");
      return;
    }
    if (active === "consultation" && (!form.childAge || !form.issue)) {
      toast.error("Please share your child's age and briefly describe the issue you're facing.");
      return;
    }
    setLoading(true);
    try {
      if (form.promo.trim()) {
        const { data } = await quotePromo({ package_id: packageId, code: form.promo.trim() });
        setPromo(data);
        toast.success(data.free ? "Code applied, your session is free!" : `Code applied, ${data.discount_pct}% off!`);
      } else {
        setPromo(null);
      }
      setStage("schedule");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply that promo code.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: payment after the slot is blocked
  const startPayment = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("script");
      const { data } = await createServiceOrder({
        package_id: packageId, email: form.email, name: form.name,
        whatsapp: form.whatsapp, child_age: form.childAge || undefined, issue: form.issue || undefined,
        promo_code: promo && !promo.free ? form.promo.trim() : undefined,
        origin_url: window.location.origin,
      });
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "Mended Alchemy", description: SERVICES[active].name, order_id: data.order_id,
        prefill: { name: form.name, email: form.email, contact: form.whatsapp },
        theme: { color: "#2F3B2E" },
        modal: { ondismiss: () => setLoading(false) },
        handler: async (r) => {
          try {
            await verifyServicePayment({
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
              email: form.email,
            });
            setStage("thanks");
            toast.success("Payment complete! Your session is confirmed.");
          } catch {
            setStage("thanks");
            toast.success("Payment received! We're confirming it and will email you shortly.");
          } finally { setLoading(false); }
        },
      });
      rzp.on("payment.failed", () => { toast.error("Your payment wasn't completed. Your time slot is held, please try again."); setLoading(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      toast.success("Link copied, share it anywhere you like.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy automatically, please copy the address from your browser.");
    }
  };

  const svc = active ? SERVICES[active] : null;
  const firstName = (form.name || "Mumma").split(" ")[0];
  const shareText = svc
    ? encodeURIComponent(`I just booked "${svc.name}" with Mended Alchemy, life & parenting coaching by Priya Rawal. Grow within. Parent consciously. Thrive together. ${window.location.origin}/?service=${active}`)
    : "";
  const whatsAppShare = `https://wa.me/?text=${shareText}`;

  const inputCls = "mt-2 w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors";
  const labelCls = "font-mono-accent text-xs uppercase tracking-[0.18em] text-[#2F3B2E]/70";

  return (
    <section id="services-work" data-testid="services-work-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12 bg-[#F3EEE3] border-y border-[#2F3B2E]/10">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Label>Services · Work With Me</Label>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-[#3B352D]">
              Two ways to begin, whenever you're ready
            </h2>
          </div>
          <CurrencyToggle currency={currency} onChange={setCurrency} />
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {["transformation", "consultation"].map((id, i) => {
            const s = SERVICES[id];
            return (
              <Reveal key={id} delay={i * 0.1} className="h-full">
                <div data-testid={`service-work-card-${id}`} className={`relative h-full flex flex-col rounded-3xl p-8 lg:p-10 border transition-colors duration-300 ${s.flagship ? "bg-[#2F3B2E] text-white border-[#2F3B2E] shadow-[0_30px_70px_-25px_rgba(47,59,46,0.6)]" : "bg-white text-[#3B352D] border-[#2F3B2E]/12"}`}>
                  {s.flagship && (
                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-[#A8916B] px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white">
                      <Sparkles size={12} /> Flagship Journey
                    </span>
                  )}
                  <p className={`font-mono-accent text-[11px] uppercase tracking-[0.15em] ${s.flagship ? "text-[#C9B48A]" : "text-[#2F3B2E]/60"}`}>{s.tagline}</p>
                  <h3 className={`mt-3 font-serif-display text-3xl font-medium ${s.flagship ? "text-white" : "text-[#2F3B2E]"}`}>{s.name}</h3>
                  <p data-testid={`service-work-price-${id}`} className={`mt-3 font-serif-display text-4xl ${s.flagship ? "text-[#F2EAD9]" : "text-[#2F3B2E]"}`}>{s.prices[currency].label}</p>
                  <p className={`mt-4 text-base leading-relaxed ${s.flagship ? "text-white/80" : "text-[#6B665C]"}`}>{s.lead}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check size={17} className={s.flagship ? "text-[#C9B48A] shrink-0 mt-0.5" : "text-[#A8916B] shrink-0 mt-0.5"} />
                        <span className={s.flagship ? "text-white/90" : "text-[#3B352D]/85"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => open(id)} data-testid={`service-work-cta-${id}`} className={`mt-8 w-full rounded-full py-3.5 text-sm font-medium transition-colors duration-200 ${s.flagship ? "bg-[#A8916B] text-white hover:bg-[#8A7656]" : "bg-[#2F3B2E] text-white hover:bg-[#232B22]"}`}>{s.cta}</button>
                  <p className={`mt-3 text-center text-xs ${s.flagship ? "text-white/60" : "text-[#6B665C]"}`}>{s.sub}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-6 text-center text-xs text-[#6B665C]" data-testid="services-work-note">
          Pick your time first, pay after · Prices in INR or USD · Children are not required to attend any session
        </p>

        {/* Booking flow overlay */}
        {active && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="service-booking-overlay">
            <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#FAF7F0] border border-[#2F3B2E]/15 p-7 lg:p-9">
              <button onClick={() => setActive(null)} data-testid="service-booking-close" aria-label="Close booking" className="absolute top-5 right-5 text-[#2F3B2E]/60 hover:text-[#2F3B2E] text-sm z-10">Close ✕</button>

              {stage === "choose" && (
                <>
                  <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#A8916B]">{svc.tagline}</p>
                  <h3 className="mt-2 font-serif-display text-2xl sm:text-3xl font-medium text-[#3B352D]">{svc.name}</h3>
                  <p className="mt-1 font-serif-display text-2xl text-[#2F3B2E]">
                    {svc.prices[currency].label}
                    {promo && !promo.free && <span className="ml-3 text-lg text-[#A8916B]">→ {fmt(promo.final_amount)} with code</span>}
                    {promo?.free && <span className="ml-3 text-lg text-[#A8916B]">→ Free with code</span>}
                  </p>

                  <div className="mt-5 space-y-4" data-testid="service-details">
                    {svc.details.map((d, i) => (
                      <p key={i} className="text-sm leading-relaxed text-[#3B352D]/85">{d}</p>
                    ))}
                    <div className="rounded-2xl border border-[#2F3B2E]/12 bg-white p-5">
                      <p className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#2F3B2E]/70">{svc.listTitle}</p>
                      <ul className="mt-3 grid sm:grid-cols-2 gap-x-5 gap-y-2">
                        {svc.list.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-[#3B352D]/85">
                            <Check size={14} className="text-[#A8916B] shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-sm leading-relaxed text-[#3B352D]/85 italic">{svc.closing}</p>
                    <p className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A8916B]">{svc.format}</p>
                  </div>

                  <div className="mt-6 pt-5 border-t border-[#2F3B2E]/10 space-y-4">
                    <div><label className={labelCls}>Full name *</label>
                      <input data-testid="service-name" className={inputCls} value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} placeholder="Your name" /></div>
                    <div><label className={labelCls}>Email *</label>
                      <input data-testid="service-email" type="email" className={inputCls} value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))} placeholder="you@email.com" /></div>
                    <div><label className={labelCls}>WhatsApp / Mobile *</label>
                      <input data-testid="service-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e)=>setForm(f=>({...f,whatsapp:e.target.value}))} placeholder="+91 98765 43210" /></div>
                    {active === "consultation" && (
                      <>
                        <div><label className={labelCls}>Age of your child *</label>
                          <select data-testid="service-child-age" className={inputCls} value={form.childAge} onChange={(e)=>setForm(f=>({...f,childAge:e.target.value}))}>
                            <option value="">Select age range</option>
                            {CHILD_AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select></div>
                        <div><label className={labelCls}>Briefly describe the issue you're facing *</label>
                          <textarea data-testid="service-issue" rows={3} className={inputCls + " resize-none"} value={form.issue} onChange={(e)=>setForm(f=>({...f,issue:e.target.value}))} placeholder="What's been happening, and what would you like help with?" /></div>
                      </>
                    )}
                    <div><label className={labelCls}>Promo code (optional)</label>
                      <input data-testid="service-promo" className={inputCls} value={form.promo} onChange={(e)=>setForm(f=>({...f,promo:e.target.value}))} placeholder="Have a code? Enter it here" /></div>
                  </div>

                  <button onClick={proceedToSchedule} disabled={loading || !calendlyUrl} data-testid="service-pay-button" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] py-3.5 text-sm font-medium text-white hover:bg-[#232B22] transition-colors disabled:opacity-70">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
                    {loading ? "Please wait…" : "Continue, choose your session time"}
                  </button>
                  <p className="mt-3 text-center text-xs text-[#6B665C]">You pick your time first · Payment comes right after · Children are not required to attend</p>
                </>
              )}

              {stage === "schedule" && (
                <div data-testid="service-schedule">
                  <div className="text-center mb-3">
                    <h3 className="font-serif-display text-xl sm:text-2xl font-medium text-[#3B352D]">Block your time</h3>
                    <p className="mt-1 text-xs sm:text-sm text-[#6B665C]">Pick a slot below — payment comes right after.</p>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-[#2F3B2E]/15 bg-white">
                    {calUrl ? (
                      <iframe title="Schedule your session" src={calUrl} className="w-full" style={{ height: "clamp(430px, 58vh, 560px)", border: 0 }} data-testid="calendly-embed" />
                    ) : (
                      <div className="flex items-center justify-center" style={{ height: "430px" }} data-testid="calendly-loading">
                        <Loader2 size={28} className="animate-spin text-[#2F3B2E]/50" />
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-center text-xs text-[#6B665C]">Can't see the calendar? <a href={calendlyUrl} target="_blank" rel="noreferrer" className="underline">Open scheduling in a new tab</a>.</p>
                </div>
              )}

              {stage === "pay" && (
                <div data-testid="service-pay-stage" className="text-center">
                  <CreditCard className="mx-auto text-[#2F3B2E] mb-3" size={26} />
                  <h3 className="font-serif-display text-2xl sm:text-3xl font-medium text-[#3B352D]">Your time is blocked, {firstName}.</h3>
                  <p className="mt-2 text-sm text-[#6B665C] max-w-md mx-auto">
                    One last step, complete the payment to confirm your {active === "transformation" ? "journey" : "session"}.
                  </p>
                  <div className="mt-6 rounded-2xl border border-[#2F3B2E]/15 bg-white p-6">
                    <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#2F3B2E]/60">{svc.name}</p>
                    {promo && !promo.free ? (
                      <p className="mt-2 font-serif-display text-3xl text-[#2F3B2E]">
                        <span className="line-through text-[#6B665C]/60 text-xl mr-2">{svc.prices[currency].label}</span>
                        <span data-testid="service-final-price">{fmt(promo.final_amount)}</span>
                        <span className="ml-2 align-middle rounded-full bg-[#A8916B]/15 text-[#A8916B] px-2.5 py-1 text-[11px] font-mono-accent uppercase tracking-wider">{promo.discount_pct}% off</span>
                      </p>
                    ) : (
                      <p className="mt-2 font-serif-display text-3xl text-[#2F3B2E]" data-testid="service-final-price">{svc.prices[currency].label}</p>
                    )}
                  </div>
                  <button onClick={startPayment} disabled={loading} data-testid="service-pay-now" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] py-3.5 text-sm font-medium text-white hover:bg-[#232B22] transition-colors disabled:opacity-70">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {loading ? "Opening secure checkout…" : `Pay ${promo && !promo.free ? fmt(promo.final_amount) : svc.prices[currency].label} securely`}
                  </button>
                  <p className="mt-3 text-center text-xs text-[#6B665C]">Secure payment via Razorpay · Your slot stays held while you pay</p>
                </div>
              )}

              {stage === "thanks" && (
                <div data-testid="service-thanks">
                  <div className="rounded-3xl bg-[#2F3B2E] text-white px-7 py-10 text-center">
                    <img src="/logo.jpeg" alt="Mended Alchemy" className="mx-auto h-16 w-16 rounded-full border-2 border-[#C9B48A]" />
                    <p className="mt-4 font-mono-accent text-[11px] uppercase tracking-[0.25em] text-[#C9B48A]">Mended Alchemy · Life &amp; Parenting Coaching</p>
                    <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl font-medium">Your journey has begun, {firstName}.</h3>
                    <p className="mt-3 text-sm text-white/80 max-w-md mx-auto leading-relaxed">
                      {active === "transformation"
                        ? "Your time is blocked and your six-session journey is confirmed. Six sessions of gentle, honest work await you."
                        : "Your time is blocked and your session is confirmed. Come as you are, bring the question that's been weighing on your heart."}
                    </p>
                    <p className="mt-6 font-serif-display text-lg italic text-[#F2EAD9]">"Grow within. Parent consciously. Thrive together."</p>
                  </div>
                  <p className="mt-4 text-center text-xs text-[#6B665C]">Screenshot this card to keep it, or share your brave first step.</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <a href={whatsAppShare} target="_blank" rel="noreferrer" data-testid="service-thanks-whatsapp" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] py-3 text-sm font-medium text-white hover:bg-[#232B22] transition-colors">
                      <MessageCircle size={16} /> Share on WhatsApp
                    </a>
                    <button onClick={copyShareLink} data-testid="service-thanks-copy" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#2F3B2E]/25 py-3 text-sm font-medium text-[#2F3B2E] hover:bg-[#2F3B2E] hover:text-white transition-colors">
                      {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Link copied" : "Copy link"}
                    </button>
                  </div>
                  <button onClick={() => setActive(null)} data-testid="service-thanks-close" className="mt-3 w-full rounded-full py-3 text-sm font-medium text-[#2F3B2E]/60 hover:text-[#2F3B2E] transition-colors">Close</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
